/**
 * Models API
 *
 * API calls for model configuration
 * Reference: api/services/models_service.py, api/routers/admin/models.py
 */

import { apiClient } from './client';
import type { ModelInfo, AllModelsResponse, ModelsHealthStatus } from '@/types';

const BASE_PATH = '/models';

export const modelsApi = {
  /**
   * Get all available chat models
   */
  listChatModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/chat`);
    return response.data;
  },

  /**
   * Get all available memory/summary models
   */
  listMemoryModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/memory`);
    return response.data;
  },

  /**
   * Get all available embedding models
   */
  listEmbeddingModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/embedding`);
    return response.data;
  },

  /**
   * Get all available vision/multimodal models
   */
  listVisionModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/vision`);
    return response.data;
  },

  /**
   * Get all available reasoning/extended thinking models
   */
  listReasoningModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/reasoning`);
    return response.data;
  },

  /**
   * Get all available text-to-speech models
   */
  listTtsModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/tts`);
    return response.data;
  },

  /**
   * Get all available speech-to-text models
   */
  listSttModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/stt`);
    return response.data;
  },

  /**
   * Get all available reranking models
   */
  listRerankingModels: async (): Promise<ModelInfo[]> => {
    const response = await apiClient.get(`${BASE_PATH}/reranking`);
    return response.data;
  },

  /**
   * Get all model options in a single call (recommended for initial load)
   */
  listAllModels: async (): Promise<AllModelsResponse> => {
    const response = await apiClient.get(`${BASE_PATH}/all`);
    return response.data;
  },

  /**
   * Get detailed information about a specific model
   */
  getModel: async (modelId: string): Promise<ModelInfo> => {
    const response = await apiClient.get(`${BASE_PATH}/${encodeURIComponent(modelId)}`);
    return response.data;
  },

  /**
   * Get models service health status
   */
  getHealth: async (): Promise<ModelsHealthStatus> => {
    const response = await apiClient.get(`${BASE_PATH}/health`);
    return response.data;
  },

  /**
   * Force refresh the models cache
   */
  refreshCache: async (): Promise<{ success: boolean; message: string; models_count: number }> => {
    const response = await apiClient.post(`${BASE_PATH}/refresh`);
    return response.data;
  },
};
